import isEqual from "lodash.isequal";

import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "@src/store/rootState";
import { getAgProducts } from "@src/store/slices/agProducts";
import { AgProduct } from "@src/store/slices/agProducts/types";
import { parsePrice } from "@src/store/slices/agProducts/util/parsePrice";
import { getAnalyticsProducts } from "@src/store/slices/analyticsProducts";

export const getUnsavedAgProducts = createSelector(
  getAgProducts,
  (state: RootState) => state.agProducts.storedAgProducts,
  (agProducts, storedAgProducts) => {
    const normalized = normalizeProducts(agProducts);
    const normalizedStored = normalizeProducts(storedAgProducts);
    return !isEqual(normalized, normalizedStored) ? agProducts : undefined;
  }
);

function normalizeProducts(products: AgProduct[] | undefined) {
  if (!Array.isArray(products)) {
    return undefined;
  }

  return products.reduce(
    (map, product) => {
      map[product.variantId] = normalizeProduct(product);
      return map;
    },
    {} as Record<string, unknown>
  );
}

function normalizeProduct(product: AgProduct) {
  return {
    prices: normalizePrices(product.variationPrices),
  };
}

function normalizePrices(prices: AgProduct["variationPrices"]) {
  return Object.entries(prices).reduce(
    (map, [id, current]) => {
      map[id] = normalizePrice(current);
      return map;
    },
    {} as Record<string, ReturnType<typeof normalizePrice>>
  );
}

function normalizePrice(price: AgProduct["variationPrices"][string]) {
  return {
    ...price,
    price: normalizeNumber(price.price),
    ogPrice: normalizeNumber(price.ogPrice),
    compareAtPrice: normalizeNumber(price.compareAtPrice),
    ogComparePrice: normalizeNumber(price.ogCompareAtPrice),
    subscriptionDiscount: normalizeNumber(price.subscriptionDiscount),
    subscriptionDiscountType: price.subscriptionDiscountType,
  };
}

function normalizeNumber(value: string | number | null | undefined) {
  return parsePrice(value);
}
